/* MOOOD — infinite catalog (Section Rendering API, vanilla JS)
   - 24 products per batch, IntersectionObserver auto-loads the first N batches, then a "Load more" button
   - Skeleton cards while loading
   - ?page= (loaded up to), ?filter= (chip id), ?sort_by= kept in the URL so Back restores the same place
   - Chips + sort swap the source instantly without a page reload (responses cached in memory) */
(function () {
  if (customElements.get('moood-catalog')) return;

  var PAGE_SIZE = 24;
  var cache = new Map();

  function skeletonHTML(n) {
    var one = '<li class="skeleton" aria-hidden="true" data-skeleton><div class="skeleton__media"></div><div class="skeleton__line"></div><div class="skeleton__line skeleton__line--lg"></div><div class="skeleton__line skeleton__line--price"></div></li>';
    return new Array(n + 1).join(one);
  }

  function fill(str, map) {
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }

  class MooodCatalog extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var d = this.dataset;
      this.grid = this.querySelector('[data-grid]');
      this.statusEl = this.querySelector('[data-status]');
      this.progressEl = this.querySelector('[data-progress]');
      this.moreBtn = this.querySelector('[data-load-more]');
      this.sentinel = this.querySelector('[data-sentinel]');
      this.countEls = this.querySelectorAll('[data-count]');
      this.sortEl = this.querySelector('[data-sort]');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-chip]'));
      this.sectionId = d.section || 'product-grid-page';
      this.autoBatches = parseInt(d.autoBatches, 10) || 3;
      this.syncUrl = d.syncUrl === 'true';
      this.extraParams = d.params || '';
      try { this.countForms = JSON.parse(d.countForms || '{}'); } catch (e) { this.countForms = {}; }

      this.baseUrl = d.baseUrl;
      this.renderedSort = d.renderedSort || '';
      this.defaultSort = d.defaultSort || '';
      this.state = {
        url: this.baseUrl,
        section: this.sectionId,
        filter: '',
        sort: this.sortEl ? this.sortEl.value : this.renderedSort,
        from: parseInt(d.startPage, 10) || 1,
        to: parseInt(d.startPage, 10) || 1,
        pages: parseInt(d.totalPages, 10) || 1,
        total: parseInt(d.totalItems, 10) || 0
      };
      this.loading = false;
      this.token = 0;

      this.onChip = this.onChip.bind(this);
      this.chips.forEach(function (c) { c.addEventListener('click', this.onChip); }, this);
      if (this.sortEl) this.sortEl.addEventListener('change', this.onSort.bind(this));
      if (this.moreBtn) this.moreBtn.addEventListener('click', this.loadNext.bind(this, true));

      window.addEventListener('pagehide', this.saveScroll.bind(this));
      this.addEventListener('click', function (e) {
        if (e.target.closest('a[href]')) this.saveScroll();
      }.bind(this), true);

      this.restore().then(this.observe.bind(this));
    }

    /* ---------- URL + scroll state ---------- */
    saveScroll() {
      if (!this.syncUrl) return;
      try {
        var st = Object.assign({}, history.state || {}, { mooodScroll: window.scrollY });
        history.replaceState(st, '');
      } catch (e) {}
    }

    writeUrl() {
      if (!this.syncUrl) return;
      var u = new URL(location.href);
      if (this.state.to > 1) u.searchParams.set('page', this.state.to); else u.searchParams.delete('page');
      if (this.state.filter) u.searchParams.set('filter', this.state.filter); else u.searchParams.delete('filter');
      if (this.state.sort && this.state.sort !== this.defaultSort) u.searchParams.set('sort_by', this.state.sort);
      else u.searchParams.delete('sort_by');
      try { history.replaceState(history.state, '', u.toString()); } catch (e) {}
    }

    async restore() {
      if (!this.syncUrl) return this.updateUi();
      var p = new URLSearchParams(location.search);
      var target = Math.max(1, parseInt(p.get('page'), 10) || 1);
      var filter = p.get('filter') || '';
      var sort = p.has('sort_by') ? p.get('sort_by') : this.state.sort;
      var saved = history.state && typeof history.state.mooodScroll === 'number' ? history.state.mooodScroll : null;
      if (saved != null && 'scrollRestoration' in history) history.scrollRestoration = 'manual';

      var chip = this.chips.find(function (c) { return c.dataset.chip === filter; });
      if (filter && !chip) filter = '';

      try {
        if (filter || sort !== this.state.sort) {
          if (chip) this.pressChip(chip);
          this.state.filter = filter;
          this.state.url = chip ? chip.dataset.url : this.baseUrl;
          this.state.section = (chip && chip.dataset.section) || this.sectionId;
          this.state.sort = sort || '';
          if (this.sortEl) this.sortEl.value = this.state.sort;
          await this.reset(target);
        } else {
          if (this.state.from > 1) await this.loadRange(1, this.state.from - 1, 'prepend');
          if (target > this.state.to) await this.loadRange(this.state.to + 1, Math.min(target, this.state.pages), 'append');
        }
      } catch (e) { /* network hiccup: keep what's rendered */ }

      this.updateUi();
      if (saved != null) {
        requestAnimationFrame(function () { window.scrollTo(0, saved); });
      }
    }

    /* ---------- Loading ---------- */
    pageUrl(page) {
      var u = new URL(this.state.url, location.origin);
      if (this.extraParams) {
        new URLSearchParams(this.extraParams).forEach(function (v, k) { u.searchParams.set(k, v); });
      }
      u.searchParams.set('section_id', this.state.section || this.sectionId);
      u.searchParams.set('page', page);
      if (this.state.sort) u.searchParams.set('sort_by', this.state.sort);
      return u.pathname + u.search;
    }

    async fetchPage(page) {
      var url = this.pageUrl(page);
      if (!cache.has(url)) {
        cache.set(url, fetch(url, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        }).catch(function (err) { cache.delete(url); throw err; }));
      }
      var html = await cache.get(url);
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var list = doc.querySelector('[data-page-items]');
      if (!list) throw new Error('No items');
      return {
        items: Array.prototype.slice.call(list.children),
        pages: parseInt(list.dataset.pages, 10) || 1,
        total: parseInt(list.dataset.total, 10) || 0
      };
    }

    setLoading(on) {
      this.loading = on;
      this.classList.toggle('is-loading', on);
      this.grid.setAttribute('aria-busy', on ? 'true' : 'false');
      if (on && this.statusEl) this.statusEl.textContent = (window.theme && theme.strings.loading) || '…';
    }

    async loadRange(a, b, mode) {
      for (var p = a; p <= b; p++) {
        await this.loadPage(p, mode === 'prepend' ? 'prepend-' + (p - a) : 'append');
      }
    }

    async loadPage(page, mode) {
      var token = this.token;
      var remaining = Math.max(0, this.state.total - this.grid.querySelectorAll('li:not([data-skeleton])').length);
      var skeletons = Math.min(PAGE_SIZE, remaining || PAGE_SIZE, 8);
      var holder = document.createElement('template');
      holder.innerHTML = skeletonHTML(skeletons);
      var skel = Array.prototype.slice.call(holder.content.children);
      var anchor = null;
      if (mode && mode.indexOf('prepend') === 0) {
        var idx = parseInt(mode.split('-')[1], 10) || 0;
        anchor = this.grid.children[idx * PAGE_SIZE] || null;
        skel.forEach(function (s) { this.grid.insertBefore(s, anchor); }, this);
      } else {
        skel.forEach(function (s) { this.grid.appendChild(s); }, this);
      }
      this.setLoading(true);
      try {
        var res = await this.fetchPage(page);
        if (token !== this.token) return;
        var frag = document.createDocumentFragment();
        res.items.forEach(function (li) { frag.appendChild(document.importNode(li, true)); });
        var first = skel[0];
        if (first && first.parentNode) first.parentNode.insertBefore(frag, first);
        skel.forEach(function (s) { s.remove(); });
        this.state.pages = res.pages;
        this.state.total = res.total;
        if (!mode || mode === 'append') this.state.to = Math.max(this.state.to, page);
        else this.state.from = Math.min(this.state.from, page);
        document.dispatchEvent(new CustomEvent('moood:content', { detail: { root: this.grid } }));
      } catch (err) {
        skel.forEach(function (s) { s.remove(); });
        if (token === this.token) this.showError();
        throw err;
      } finally {
        if (token === this.token) this.setLoading(false);
      }
    }

    async loadNext(fromButton) {
      if (this.loading || this.state.to >= this.state.pages) return;
      try {
        await this.loadPage(this.state.to + 1, 'append');
        this.writeUrl();
      } catch (e) {}
      this.updateUi();
      if (fromButton === true && this.moreBtn && !this.moreBtn.hidden) this.moreBtn.focus();
    }

    async reset(targetPage) {
      this.token++;
      var token = this.token;
      this.state.from = 1;
      this.state.to = 0;
      this.grid.innerHTML = skeletonHTML(8);
      this.setLoading(true);
      try {
        var res = await this.fetchPage(1);
        if (token !== this.token) return;
        this.grid.innerHTML = '';
        var frag = document.createDocumentFragment();
        res.items.forEach(function (li) { frag.appendChild(document.importNode(li, true)); });
        this.grid.appendChild(frag);
        if (!res.items.length) {
          this.grid.innerHTML = '<li class="catalog__empty">' + ((window.theme && theme.strings.noResults) || '') + '</li>';
        }
        this.state.pages = res.pages;
        this.state.total = res.total;
        this.state.to = 1;
        this.setLoading(false);
        document.dispatchEvent(new CustomEvent('moood:content', { detail: { root: this.grid } }));
        var target = Math.min(targetPage || 1, res.pages);
        if (target > 1) await this.loadRange(2, target, 'append');
      } catch (err) {
        if (token === this.token) { this.grid.innerHTML = ''; this.showError(); this.setLoading(false); }
      }
      if (token !== this.token) return;
      this.writeUrl();
      this.updateUi();
    }

    showError() {
      if (!this.statusEl) return;
      this.statusEl.textContent = (window.theme && theme.strings.loadError) || 'Error';
      if (this.moreBtn && this.state.to < this.state.pages) this.moreBtn.hidden = false;
    }

    /* ---------- UI ---------- */
    updateUi() {
      var shown = this.grid.querySelectorAll('li:not([data-skeleton]):not(.catalog__empty)').length;
      var total = this.state.total;
      var s = (window.theme && theme.strings) || {};
      var auto = this.state.to < this.autoBatches;
      var done = this.state.to >= this.state.pages || shown >= total;

      if (this.statusEl) {
        if (done) this.statusEl.textContent = fill(s.allLoaded, { total: total });
        else if (auto) this.statusEl.textContent = fill(s.showingAuto, { shown: shown, total: total });
        else this.statusEl.textContent = fill(s.showing, { shown: shown, total: total });
      }
      if (this.progressEl) this.progressEl.style.width = (total ? Math.min(100, Math.round(shown / total * 100)) : 100) + '%';
      if (this.moreBtn) this.moreBtn.hidden = done || auto;
      this.countEls.forEach(function (el) { el.textContent = this.countLabel(total); }, this);
    }

    countLabel(n) {
      var f = this.countForms;
      var cat = 'other';
      try { cat = new Intl.PluralRules(document.documentElement.lang || 'ar').select(n); } catch (e) {}
      if (n === 0 && f.zero) return f.zero;
      var str = f[cat] || f.other || String(n);
      return str.replace('[n]', n.toLocaleString(document.documentElement.lang || undefined));
    }

    observe() {
      if (!this.sentinel || !('IntersectionObserver' in window)) { this.updateUi(); return; }
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting && !self.loading && self.state.to < self.autoBatches && self.state.to < self.state.pages) {
            self.loadNext().then(function () {
              // Keep going if the sentinel is still visible (tall screens)
              self.io.unobserve(self.sentinel); self.io.observe(self.sentinel);
            });
          }
        });
      }, { rootMargin: '0px 0px 800px 0px' });
      this.io.observe(this.sentinel);
      this.updateUi();
    }

    /* ---------- Chips + sort ---------- */
    pressChip(chip) {
      this.chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
    }

    onChip(e) {
      var chip = e.currentTarget;
      if (chip.getAttribute('aria-pressed') === 'true') return;
      this.pressChip(chip);
      this.state.filter = chip.dataset.chip || '';
      this.state.url = chip.dataset.url || this.baseUrl;
      this.state.section = chip.dataset.section || this.sectionId;
      this.reset(1);
    }

    onSort() {
      this.state.sort = this.sortEl.value;
      this.reset(1);
    }
  }

  customElements.define('moood-catalog', MooodCatalog);
})();
